import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
    collection,
    getDocs,
    doc,
    addDoc,
    updateDoc,
    getDoc,
    deleteDoc,
    Timestamp,
    query,
    orderBy,
    limit,
    startAfter,
    where,
    QueryDocumentSnapshot,
    getCountFromServer,
    QueryConstraint,
    or,
    and,
} from 'firebase/firestore';
import { auth, db, functions } from '@/firebase';
import { httpsCallable } from 'firebase/functions';
import { formatPhoneNumber } from '@/utils/phone';

interface OrderData {
    id: string;
    userId: string; // Keep for display/logic, but populated from UserRef or userId
    userDisplayName: string;
    userPostalCode: string;
    userAddress: string;
    userPhoneNumber: string;
    hasMemo: boolean;
    shikakuId: string;
    shikakuName: string;
    orderNumber: string;
    createdAt: Timestamp;
    expiresAt: Timestamp;
    status: string;
    course: string;
    accessories?: string;
    totalAmount?: number;
    paymentMethod?: string;
    paymentStatus?: string;
    paymentstatus?: boolean | null | string;
    total_paymentstatus?: boolean | null;
    paymentAmount?: number;
    Pstatus?: string;
    HarvDate?: Timestamp | string | null;
    TarvDate?: Timestamp | string | null;
    Hstatus?: string;
    Tstatus?: string;
    Dstatus?: string;
    TtrackingNumber?: string;
    DtrackingNumber?: string;
    isExamFinished?: boolean;
    retest1?: boolean;
    retest2?: boolean;
    diploma?: string;
    paymentItem?: PaymentItem | null;
    lost?: boolean | null;
    trouble?: boolean | null;
}

interface UserOption {
    id: string;
    display_name: string;
}

interface ShikakuOption {
    id: string;
    ShikakuName: string;
}

interface PaymentItem {
    id: string;
    dueDate?: Timestamp | string | null;
    amount?: number;
    paidAt?: Timestamp | string | null;
    status?: string;
}

interface PaymentEditForm {
    orderId: string;
    paymentId: string;
    dueDate: string;
    amount: string;
    paidAt: string;
    status: 'unpaid' | 'paid';
}

interface EditOrderForm {
    orderNumber: string;
    userId: string;
    shikakuId: string;
    TtrackingNumber: string;
    DtrackingNumber: string;
    userPostalCode: string;
    userAddress: string;
}

const PAGE_SIZE = 50;
type OrderTab = 'all' | 'shipping' | 'tensaku' | 'exam' | 'graded' | 'clickpost';

export default function OrderManagement() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [orders, setOrders] = useState<OrderData[]>([]);
    const [users, setUsers] = useState<UserOption[]>([]);
    const [shikakuList, setShikakuList] = useState<ShikakuOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [orderCursors, setOrderCursors] = useState<QueryDocumentSnapshot[]>([]);
    const [orderPage, setOrderPage] = useState(1);
    const [hasNextOrderPage, setHasNextOrderPage] = useState(false);
    const [orderTotalCount, setOrderTotalCount] = useState(0);
    const [orderNumberFilter, setOrderNumberFilter] = useState('');
    const [activeOrderNumberFilter, setActiveOrderNumberFilter] = useState('');
    const [isAddingOrder, setIsAddingOrder] = useState(false);
    const [activeTab, setActiveTab] = useState<OrderTab>('all');
    const [newOrderForm, setNewOrderForm] = useState({
        userId: '',
        shikakuId: '',
        status: 'active',
        course: '通信講座＋試験',
        diploma: '通常版',
        paymentMethod: 'クレジット',
        totalAmount: 0,
        isExamFinished: false
    });
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState<{
        users: { created: number; updated: number; errors: number; errorDetails: string[] };
        orders: { created: number; updated: number; errors: number; errorDetails: string[] };
    } | null>(null);
    const [editingOrderId, setEditingOrderId] = useState<string | null>(null);
    const [editOrderForm, setEditOrderForm] = useState<EditOrderForm | null>(null);
    const [paymentEditForm, setPaymentEditForm] = useState<PaymentEditForm | null>(null);
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [isPaymentSaving, setIsPaymentSaving] = useState(false);
    const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
    const [showAllGradedOrders, setShowAllGradedOrders] = useState(false);

    const applyOrderNumberSearch = () => {
        const trimmed = orderNumberFilter.trim();
        setActiveOrderNumberFilter(trimmed);
    };

    const renderMemoBadge = (hasMemo: boolean) => {
        const badgeClass = hasMemo ? 'bg-emerald-500 text-white' : 'bg-gray-300 text-gray-600';
        const label = hasMemo ? 'メモあり' : 'メモなし';
        return (
            <span
                className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] ${badgeClass}`}
                title={label}
                aria-label={label}
            >
                <i className="ri-sticky-note-line" />
            </span>
        );
    };

    const renderOrderUserCell = (order: OrderData, options?: { textClassName?: string; linkClassName?: string }) => {
        const textClassName = options?.textClassName ?? 'text-gray-800';
        const linkClassName = options?.linkClassName ?? 'text-blue-600 hover:text-blue-800 hover:underline';
        return (
            <div className={`flex items-center gap-2 ${textClassName}`}>
                {renderMemoBadge(order.hasMemo)}
                {order.userId !== '-' ? (
                    <Link
                        to={`/admin/users/${order.userId}`}
                        className={linkClassName}
                    >
                        {order.userDisplayName}
                    </Link>
                ) : (
                    <span>{order.userDisplayName}</span>
                )}
            </div>
        );
    };

    useEffect(() => {
        fetchUsers();
        fetchShikaku();
    }, []);

    useEffect(() => {
        const tab = searchParams.get('tab');
        if (tab === 'shipping' || tab === 'tensaku' || tab === 'exam' || tab === 'graded' || tab === 'clickpost' || tab === 'all') {
            setActiveTab(tab);
            return;
        }
        if (tab) {
            setActiveTab('all');
        }
    }, [searchParams]);

    useEffect(() => {
        setOrderCursors([]);
        setOrderPage(1);
        setSelectedOrderIds([]);
        fetchOrders(1, activeOrderNumberFilter, activeTab);
        fetchOrderTotalCount(activeOrderNumberFilter, activeTab);
    }, [activeTab, activeOrderNumberFilter]);

    useEffect(() => {
        if (activeTab !== 'graded') {
            setShowAllGradedOrders(false);
        }
    }, [activeTab]);

    useEffect(() => {
        setSelectedOrderIds((prev) => prev.filter((id) => orders.some((order) => order.id === id)));
    }, [orders]);

    const buildOrderFilters = (tab: OrderTab, orderNumber: string = '') => {
        const filters: QueryConstraint[] = [];
        let orFilter: QueryConstraint | null = null;
        if (orderNumber) {
            filters.push(where('orderNumber', '==', orderNumber));
        }
        if (tab === 'shipping') {
            filters.push(where('status', '==', 'active'));
            filters.push(where('Pstatus', '==', null));
        } else if (tab === 'tensaku') {
            filters.push(where('Tstatus', '==', 'arrive'));
        } else if (tab === 'exam') {
            filters.push(where('Hstatus', '==', 'arrive'));
        } else if (tab === 'graded') {
            orFilter = or(where('Dstatus', '==', 'ready'), where('Tstatus', '==', 'arrive'));
        } else if (tab === 'clickpost') {
            filters.push(where('status', '==', 'active'));
            orFilter = or(
                where('Pstatus', '==', null),
                where('Tstatus', '==', 'arrive'),
                where('Dstatus', '==', 'ready')
            );
        }
        if (orFilter && filters.length > 0) {
            return [and(...filters, orFilter)];
        }
        if (orFilter) {
            return [orFilter];
        }
        return filters;
    };

    const buildOrderSort = (tab: OrderTab) => {
        if (tab === 'shipping') {
            return [orderBy('Pstatus'), orderBy('createdAt', 'desc')];
        }
        if (tab === 'tensaku') {
            return [orderBy('TarvDate', 'desc')];
        }
        if (tab === 'exam') {
            return [orderBy('HarvDate', 'desc')];
        }
        return [orderBy('createdAt', 'desc')];
    };

    const fetchOrderTotalCount = async (orderNumber: string = '', tab: OrderTab = activeTab) => {
        try {
            const baseQuery = collection(db, 'Order');
            const snapshot = await getCountFromServer(query(baseQuery, ...buildOrderFilters(tab, orderNumber)));
            setOrderTotalCount(snapshot.data().count);
        } catch (error) {
            console.error('Error fetching order total count:', error);
        }
    };

    const fetchOrders = async (page: number = 1, orderNumber: string = '', tab: OrderTab = activeTab) => {
        try {
            setLoading(true);

            const baseQuery = [collection(db, 'Order'), ...buildOrderFilters(tab, orderNumber), ...buildOrderSort(tab)];
            const cursor = page > 1 ? orderCursors[page - 2] : undefined;
            const ordersSnapshot = cursor
                ? await getDocs(query(...baseQuery, startAfter(cursor), limit(PAGE_SIZE)))
                : await getDocs(query(...baseQuery, limit(PAGE_SIZE)));
            console.log('Total orders found:', ordersSnapshot.docs.length);

            const ordersData = await Promise.all(
                ordersSnapshot.docs.map(async (orderDoc) => {
                    try {
                        const orderData = orderDoc.data();
                        console.log('Processing order:', orderDoc.id, orderData);

                        // User情報を取得 (UserRef優先、なければuserId)
                        let userDisplayName = '-';
                        let userIdStr = '-';
                        let userPostalCode = '-';
                        let userAddress = '-';
                        let userPhoneNumber = '-';
                        const userRef = orderData.UserRef || orderData.userId;

                        if (userRef) {
                            try {
                                // userRefが参照型か確認
                                const userDocRef = userRef.path ? userRef : doc(db, 'User', userRef);
                                const userDoc = await getDoc(userDocRef);
                                if (userDoc.exists()) {
                                    const userData = userDoc.data() as any;
                                    userDisplayName = userData?.display_name || '-';
                                    userPostalCode = userData?.postalCode || userData?.postal_code || '-';
                                    userAddress = userData?.address || userData?.address1 || '-';
                                    userPhoneNumber = userData?.phoneNumber || userData?.phone_number || '-';
                                    userIdStr = userDoc.id;
                                }
                            } catch (error) {
                                console.error('Error fetching user for order', orderDoc.id, ':', error);
                            }
                        }

                        // Shikaku情報を取得
                        let shikakuName = '-';
                        if (orderData.shikakuId) {
                            try {
                                const shikakuDoc = await getDoc(orderData.shikakuId);
                                shikakuName = shikakuDoc.exists() ? ((shikakuDoc.data() as any)?.ShikakuName || '-') : '-';
                            } catch (error) {
                                console.error('Error fetching shikaku for order', orderDoc.id, ':', error);
                            }
                        }

                        const paymentItem: PaymentItem | null = null;

                        let hasMemo = false;
                        try {
                            const memoSnapshot = await getDocs(
                                query(
                                    collection(db, 'Memo'),
                                    where('TGTorder', '==', orderDoc.ref),
                                    limit(1)
                                )
                            );
                            hasMemo = !memoSnapshot.empty;
                        } catch (error) {
                            console.error('Error fetching memo for order', orderDoc.id, ':', error);
                        }

                        return {
                            id: orderDoc.id,
                            userId: userIdStr,
                            userDisplayName,
                            userPostalCode,
                            userAddress,
                            userPhoneNumber,
                            hasMemo,
                            shikakuId: orderData.shikakuId?.id || '-',
                            shikakuName,
                            orderNumber: orderData.orderNumber || '-',
                            createdAt: orderData.createdAt,
                            expiresAt: orderData.expiresAt,
                            status: orderData.status || 'active',
                            course: orderData.course ? String(orderData.course) : '-',
                            accessories: orderData.accessories ? String(orderData.accessories) : '-',
                            totalAmount: orderData.totalAmount ?? 0,
                            Pstatus: orderData.Pstatus || '',
                            paymentMethod: orderData.paymentMethod || '',
                            paymentStatus: orderData.paymentStatus || '',
                            paymentstatus: orderData.paymentstatus ?? null,
                            total_paymentstatus: orderData.total_paymentstatus ?? null,
                            paymentAmount: orderData.paymentAmount ?? 0,
                            HarvDate: orderData.HarvDate ?? null,
                            TarvDate: orderData.TarvDate ?? null,
                            Hstatus: orderData.Hstatus || '',
                            Tstatus: orderData.Tstatus || '',
                            Dstatus: orderData.Dstatus || '',
                            TtrackingNumber: orderData.TtrackingNumber || '-',
                            DtrackingNumber: orderData.DtrackingNumber || '-',
                            isExamFinished: orderData.isExamFinished || false,
                            retest1: orderData.retest1 || false,
                            retest2: orderData.retest2 || false,
                            diploma: orderData.diploma ? String(orderData.diploma) : '-',
                            paymentItem,
                            lost: orderData.lost ?? null,
                            trouble: orderData.trouble ?? null,
                        };
                    } catch (error) {
                        console.error('Error processing order:', orderDoc.id, error);
                        return null;
                    }
                })
            );

            // Filter out null values
            const validOrders = ordersData.filter((order) => {
                if (!order) return false;
                if (tab === 'graded') {
                    return order.lost !== true && order.trouble !== true;
                }
                if (tab === 'clickpost') {
                    return order.status === 'active' && order.trouble !== true;
                }
                return true;
            }) as OrderData[];
            console.log('Valid orders:', validOrders.length, validOrders);
            setOrders(validOrders);
            setOrderPage(page);
            setHasNextOrderPage(ordersSnapshot.docs.length === PAGE_SIZE);

            if (ordersSnapshot.docs.length > 0) {
                setOrderCursors((prev) => {
                    const updated = [...prev];
                    updated[page - 1] = ordersSnapshot.docs[ordersSnapshot.docs.length - 1];
                    return updated.slice(0, page);
                });
            }
            setLoading(false);
        } catch (error) {
            console.error('Error fetching orders:', error);
            alert(`Order一覧の取得に失敗しました: ${error}`);
            setLoading(false);
        }
    };

    const getOrderDisplayRange = (filteredOrders: OrderData[]) => {
        if (orderTotalCount === 0) return '0件';
        const start = (orderPage - 1) * PAGE_SIZE + 1;
        const end = Math.min(orderPage * PAGE_SIZE, orderTotalCount);
        return `${orderTotalCount} 件中 ${start} ～ ${end} 件を表示中`;
    };

    const fetchUsers = async () => {
        try {
            const usersSnapshot = await getDocs(collection(db, 'User'));
            const usersData = usersSnapshot.docs.map(doc => ({
                id: doc.id,
                display_name: doc.data().display_name || '-'
            }));
            setUsers(usersData);
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const fetchShikaku = async () => {
        try {
            const shikakuSnapshot = await getDocs(collection(db, 'Shikaku'));
            const shikakuData = shikakuSnapshot.docs.map(doc => ({
                id: doc.id,
                ShikakuName: doc.data().ShikakuName || '-'
            }));
            setShikakuList(shikakuData);
        } catch (error) {
            console.error('Error fetching shikaku:', error);
        }
    };

    const generateOrderNumber = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
        return `ORD-${year}${month}${day}-${random}`;
    };

    const handleAddOrder = async () => {
        if (!newOrderForm.userId || !newOrderForm.shikakuId) {
            alert('ユーザーと資格を選択してください');
            return;
        }

        try {
            const createdAt = Timestamp.now();
            const expiresAt = Timestamp.fromDate(
                new Date(createdAt.toDate().getTime() + 2 * 365 * 24 * 60 * 60 * 1000) // 2年後
            );

            const shikakuRef = doc(db, 'Shikaku', newOrderForm.shikakuId);

            let examSetRef: any = null;
            const latestActiveSetQuery = query(
                collection(db, 'ExamSets'),
                where('shikakuRef', '==', shikakuRef),
                where('effectiveFrom', '<=', createdAt),
                orderBy('effectiveFrom', 'desc'),
                limit(1)
            );
            const latestActiveSetSnapshot = await getDocs(latestActiveSetQuery);
            if (!latestActiveSetSnapshot.empty) {
                examSetRef = latestActiveSetSnapshot.docs[0].ref;
            } else {
                const v0Query = query(
                    collection(db, 'ExamSets'),
                    where('shikakuRef', '==', shikakuRef),
                    where('version', '==', 0),
                    limit(1)
                );
                const v0Snapshot = await getDocs(v0Query);
                if (!v0Snapshot.empty) {
                    examSetRef = v0Snapshot.docs[0].ref;
                }
            }

            await addDoc(collection(db, 'Order'), {
                UserRef: doc(db, 'User', newOrderForm.userId),
                shikakuId: shikakuRef,
                examSetRef,
                orderNumber: generateOrderNumber(),
                createdAt,
                expiresAt,
                status: newOrderForm.status,
                course: newOrderForm.course,
                diploma: newOrderForm.diploma,
                paymentMethod: newOrderForm.paymentMethod,
                totalAmount: Number(newOrderForm.totalAmount) || 0,
                isExamFinished: newOrderForm.isExamFinished,
                retest1: false,
                retest2: false,
                trouble: false,
                lost: false,
            });

            alert('Orderを追加しました');
            setIsAddingOrder(false);
            setNewOrderForm({ userId: '', shikakuId: '', status: 'active', course: '通信講座＋試験', diploma: '通常版', paymentMethod: 'クレジット', totalAmount: 0, isExamFinished: false });
            fetchOrders(orderPage, activeOrderNumberFilter, activeTab);
            fetchOrderTotalCount(activeOrderNumberFilter, activeTab);
        } catch (error) {
            console.error('Error adding order:', error);
            alert(`追加に失敗しました: ${error}`);
        }
    };

    const handleUpdateStatus = async (orderId: string, newStatus: string) => {
        try {
            await updateDoc(doc(db, 'Order', orderId), { status: newStatus });
            alert('ステータスを更新しました');
            fetchOrders(orderPage, activeOrderNumberFilter, activeTab);
        } catch (error) {
            console.error('Error updating status:', error);
            alert('更新に失敗しました');
        }
    };

    const handleUpdateExamFinished = async (orderId: string, isFinished: boolean) => {
        try {
            await updateDoc(doc(db, 'Order', orderId), { isExamFinished: isFinished });
            fetchOrders(orderPage, activeOrderNumberFilter, activeTab);
        } catch (error) {
            console.error('Error updating exam finished status:', error);
            alert('更新に失敗しました');
        }
    };

    const handleUpdateRetestFlag = async (orderId: string, field: 'retest1' | 'retest2', value: boolean) => {
        try {
            const payload: Record<string, boolean> = {};
            payload[field] = value;
            await updateDoc(doc(db, 'Order', orderId), payload);
            fetchOrders(orderPage, activeOrderNumberFilter, activeTab);
        } catch (error) {
            console.error('Error updating retest flag:', error);
            alert('更新に失敗しました');
        }
    };

    const handleDeleteOrder = async (orderId: string) => {
        if (!confirm('このOrderを削除しますか？')) return;
        try {
            await deleteDoc(doc(db, 'Order', orderId));
            await fetchOrders(orderPage, activeOrderNumberFilter, activeTab);
            await fetchOrderTotalCount(activeOrderNumberFilter, activeTab);
        } catch (error) {
            console.error('Error deleting order:', error);
            alert('削除に失敗しました');
        }
    };

    const handleOrderNextPage = () => {
        if (hasNextOrderPage) {
            fetchOrders(orderPage + 1, activeOrderNumberFilter, activeTab);
        }
    };

    const handleOrderPrevPage = () => {
        if (orderPage > 1) {
            fetchOrders(orderPage - 1, activeOrderNumberFilter, activeTab);
        }
    };

    const buildOrderDetailLink = (orderId: string) => {
        const params = new URLSearchParams();
        if (activeTab !== 'all') {
            params.set('tab', activeTab);
        }
        const queryString = params.toString();
        return queryString ? `/admin/orders/${orderId}?${queryString}` : `/admin/orders/${orderId}`;
    };

    const startEditingOrder = (order: OrderData) => {
        if (activeTab !== 'graded') return;
        setEditingOrderId(order.id);
        setEditOrderForm({
            orderNumber: order.orderNumber ?? '',
            userId: order.userId ?? '',
            shikakuId: order.shikakuId ?? '',
            TtrackingNumber: order.TtrackingNumber ?? '',
            DtrackingNumber: order.DtrackingNumber ?? '',
            userPostalCode: order.userPostalCode ?? '',
            userAddress: order.userAddress ?? ''
        });
    };

    const handleEditChange = <K extends keyof EditOrderForm>(field: K, value: EditOrderForm[K]) => {
        setEditOrderForm((prev) => (prev ? { ...prev, [field]: value } : prev));
    };

    const handleCancelEdit = () => {
        setEditingOrderId(null);
        setEditOrderForm(null);
    };

    const handleSaveEdit = async (orderId: string) => {
        if (!editOrderForm) return;

        try {
            const orderPayload: Record<string, any> = {
                orderNumber: editOrderForm.orderNumber || '-',
                TtrackingNumber: editOrderForm.TtrackingNumber || '',
                DtrackingNumber: editOrderForm.DtrackingNumber || '',
            };

            if (editOrderForm.shikakuId && editOrderForm.shikakuId !== '-') {
                orderPayload.shikakuId = doc(db, 'Shikaku', editOrderForm.shikakuId);
            }

            if (editOrderForm.userId && editOrderForm.userId !== '-') {
                orderPayload.UserRef = doc(db, 'User', editOrderForm.userId);
                await updateDoc(doc(db, 'User', editOrderForm.userId), {
                    postalCode: editOrderForm.userPostalCode || '',
                    address: editOrderForm.userAddress || ''
                });
            }

            await updateDoc(doc(db, 'Order', orderId), orderPayload);
            await fetchOrders(orderPage, activeOrderNumberFilter, activeTab);
            setEditingOrderId(null);
            setEditOrderForm(null);
        } catch (error) {
            console.error('Error updating order:', error);
            alert('更新に失敗しました');
        }
    };

    const handleTabChange = (tab: OrderTab) => {
        setActiveTab(tab);
        setOrderCursors([]);
        setEditingOrderId(null);
        setEditOrderForm(null);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (tab === 'all') {
                next.delete('tab');
            } else {
                next.set('tab', tab);
            }
            return next;
        });
    };

    const handleLogout = async () => {
        try {
            await auth.signOut();
            navigate('/login');
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const callSyncAllFromSheets = async () => {
        const currentUser = auth.currentUser;
        if (!currentUser) {
            throw new Error('ログインが必要です');
        }

        const syncAllFromSheets = httpsCallable(functions, 'syncAllFromSheets');
        // 直接 URL を叩かず、環境に応じた callable Function へ移行（認証ヘッダー手動付与も不要）
        const response = await syncAllFromSheets();

        const responseData = response.data as { success?: boolean; users: any; orders: any };
        if (!responseData?.success) {
            const message = (responseData as any)?.error || 'Functions error';
            throw new Error(message as string);
        }

        return responseData as { users: any; orders: any };
    };

    const handleSyncFromSpreadsheet = async () => {
        if (!confirm('スプレッドシートからOrderデータを同期しますか?')) return;

                setIsSyncing(true);
        try {
            const data = await callSyncAllFromSheets();
                if (data.success) {
                    setSyncResult({ users: data.users, orders: data.orders });
                    alert(
                        `同期完了!\n[Users] 新規作成: ${data.users.created}件 / 更新: ${data.users.updated}件 / エラー: ${data.users.errors}件` +
                        `\n[Order] 新規作成: ${data.orders.created}件 / 更新: ${data.orders.updated}件 / エラー: ${data.orders.errors}件`
                    );
                    await fetchOrders(orderPage, activeOrderNumberFilter, activeTab); // Reload orders
                    await fetchOrderTotalCount(activeOrderNumberFilter, activeTab);
                } else {
                alert('同期に失敗しました');
            }
        } catch (error: any) {
            console.error('Sync error:', error);
            alert(`同期エラー: ${error.message || '不明なエラー'}`);
        } finally {
            setIsSyncing(false);
        }
    };

    const formatDateForFilename = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}${month}${day}_${hours}${minutes}`;
    };

    const escapeCsvValue = (value: string) => {
        if (value.includes('"') || value.includes(',') || value.includes('\n')) {
            return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
    };

    const formatShipmentStatus = (status?: string) => {
        if (!status) return '--';
        if (status === 'arrive') return '到着';
        if (status === 'done') return '完了';
        return status;
    };

    const formatOrderDate = (timestamp?: Timestamp | string | null) => {
        if (!timestamp) return '-';
        const date = timestamp instanceof Timestamp ? timestamp.toDate() : new Date(timestamp);
        if (Number.isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('ja-JP', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    const formatDateInputValue = (timestamp?: Timestamp | string | null) => {
        if (!timestamp) return '';
        const date = timestamp instanceof Timestamp ? timestamp.toDate() : new Date(timestamp);
        if (Number.isNaN(date.getTime())) return '';
        const offsetDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        return offsetDate.toISOString().split('T')[0];
    };

    const formatCurrency = (amount?: number) => {
        if (amount == null) return '-';
        return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(amount);
    };

    const splitAddressByHalfWidth = (address: string) => {
        const normalized = (address || '').trim();
        if (!normalized) {
            return ['', '', ''];
        }

        const lines = ['', '', ''];
        const maxCountPerLine = 40;
        let currentLine = 0;
        let currentCount = 0;

        const getCharCount = (char: string) => {
            const codePoint = char.codePointAt(0) ?? 0;
            const isAscii = codePoint <= 0x007f;
            const isHalfWidthKana = codePoint >= 0xff61 && codePoint <= 0xff9f;
            return isAscii || isHalfWidthKana ? 1 : 2;
        };

        for (const char of normalized) {
            const charCount = getCharCount(char);
            if (currentCount + charCount > maxCountPerLine) {
                currentLine += 1;
                currentCount = 0;
            }
            if (currentLine >= lines.length) {
                break;
            }
            lines[currentLine] += char;
            currentCount += charCount;
        }

        return lines;
    };

    const isOrderSelected = (orderId: string) => selectedOrderIds.includes(orderId);

    const isAllVisibleSelected = orders.length > 0 && orders.every((order) => selectedOrderIds.includes(order.id));

    const handleToggleOrderSelection = (orderId: string) => {
        setSelectedOrderIds((prev) =>
            prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
        );
    };

    const handleToggleAllVisibleSelection = () => {
        if (isAllVisibleSelected) {
            setSelectedOrderIds((prev) => prev.filter((id) => !orders.some((order) => order.id === id)));
            return;
        }
        setSelectedOrderIds((prev) => Array.from(new Set([...prev, ...orders.map((order) => order.id)])));
    };

    const openPaymentEdit = (order: OrderData) => {
        if (!order.paymentItem) {
            alert('未入金の支払いが見つかりません');
            return;
        }
        setPaymentEditForm({
            orderId: order.id,
            paymentId: order.paymentItem.id,
            dueDate: formatDateInputValue(order.paymentItem.dueDate),
            amount: order.paymentItem.amount != null ? String(order.paymentItem.amount) : '',
            paidAt: formatDateInputValue(order.paymentItem.paidAt),
            status: order.paymentItem.status === 'paid' ? 'paid' : 'unpaid'
        });
        setIsPaymentModalOpen(true);
    };

    const closePaymentModal = () => {
        setIsPaymentModalOpen(false);
        setPaymentEditForm(null);
    };

    const handlePaymentSave = async () => {
        if (!paymentEditForm) return;
        if (!paymentEditForm.dueDate) {
            alert('入金期日を入力してください');
            return;
        }
        const amountValue = Number(paymentEditForm.amount);
        if (Number.isNaN(amountValue)) {
            alert('入金額を数値で入力してください');
            return;
        }
        setIsPaymentSaving(true);
        try {
            const nextStatus = paymentEditForm.status === 'paid' ? 'paid' : 'unpaid';
            const dueDate = Timestamp.fromDate(new Date(paymentEditForm.dueDate));
            const paidAt = paymentEditForm.paidAt
                ? Timestamp.fromDate(new Date(paymentEditForm.paidAt))
                : null;
            await updateDoc(
                doc(db, 'Order', paymentEditForm.orderId, 'payments', paymentEditForm.paymentId),
                {
                    dueDate,
                    amount: amountValue,
                    paidAt,
                    status: nextStatus
                }
            );
            closePaymentModal();
            await fetchOrders(orderPage, activeOrderNumberFilter, activeTab);
        } catch (error) {
            console.error('Error updating payment:', error);
            alert('入金情報の更新に失敗しました');
        } finally {
            setIsPaymentSaving(false);
        }
    };

    const handleMarkExamDone = async (orderId: string) => {
        if (!confirm('完了しますか？')) return;
        try {
            await updateDoc(doc(db, 'Order', orderId), { Hstatus: 'done', Dstatus: 'ready', testtype: 'paper' });
            await fetchOrders(orderPage, activeOrderNumberFilter, activeTab);
        } catch (error) {
            console.error('Error updating exam status:', error);
            alert('更新に失敗しました');
        }
    };

    const handleDownloadCsv = () => {
        if (activeTab === 'all') {
            return;
        }
        if (activeTab === 'clickpost') {
            if (selectedOrderIds.length === 0) {
                alert('クリックポストCSVの対象を1件以上選択してください');
                return;
            }
            const selectedOrders = filteredOrders.filter((order) => selectedOrderIds.includes(order.id));
            if (selectedOrders.length === 0) {
                alert('選択中のデータが見つかりません');
                return;
            }
            const header = [
                'お届け先郵便番号',
                'お届け先氏名',
                'お届け先敬称',
                'お届け先住所1行目',
                'お届け先住所2行目',
                'お届け先住所3行目',
                'お届け先住所4行目',
                '内容品'
            ];

            const rows = selectedOrders.flatMap((order) => {
                const [addressLine1, addressLine2, addressLine3] = splitAddressByHalfWidth(order.userAddress || '');
                const baseRow = [
                    order.userPostalCode || '',
                    order.userDisplayName || '',
                    '様',
                    addressLine1,
                    addressLine2,
                    addressLine3,
                    '',
                    order.Pstatus === 'done' ? '書類' : '教材'
                ];

                const isPstatusUnset = order.Pstatus == null || order.Pstatus === '';
                const isDrapeOnly = (order.accessories || '').trim() === 'ドレープ';
                const shouldDuplicate = isPstatusUnset && isDrapeOnly;
                if (!shouldDuplicate) {
                    return [baseRow];
                }

                const drapeRow = [...baseRow];
                drapeRow[7] = 'ドレープ';
                return [baseRow, drapeRow];
            });

            const csvContent = [header, ...rows]
                .map((row) => row.map((value) => escapeCsvValue(String(value))).join(','))
                .join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `orders_clickpost_${formatDateForFilename(new Date())}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            return;
        }
        const isGradedTab = activeTab === 'graded';
        const csvSourceOrders = isGradedTab
            ? filteredOrders.filter((order) => order.Dstatus === 'ready')
            : filteredOrders;

        if (csvSourceOrders.length === 0) {
            alert('ダウンロードするデータがありません');
            return;
        }

        const header = isGradedTab
              ? ['注文番号', 'ユーザー', '資格', '添削（Tstatus）', '本試験（Hstatus）', '郵便番号', '住所']
              : ['注文番号', 'ユーザー', '資格', '添削追跡番号', '認定追跡番号', '郵便番号', '住所'];
        const rows = csvSourceOrders.map((order) =>
            isGradedTab
                  ? [
                        order.orderNumber || '-',
                        order.userDisplayName || '-',
                        order.shikakuName || '-',
                        formatShipmentStatus(order.Tstatus),
                        formatShipmentStatus(order.Hstatus),
                        order.userPostalCode || '-',
                        order.userAddress || '-'
                    ]
                  : [
                        order.orderNumber || '-',
                        order.userDisplayName || '-',
                        order.shikakuName || '-',
                        order.TtrackingNumber || '-',
                        order.DtrackingNumber || '-',
                        order.userPostalCode || '-',
                        order.userAddress || '-'
                    ]
        );
        const csvContent = [header, ...rows]
            .map((row) => row.map((value) => escapeCsvValue(String(value))).join(','))
            .join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `orders_${activeTab}_${formatDateForFilename(new Date())}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-lg">読み込み中...</div>
            </div>
        );
    }

    const filteredOrders = orders.filter((order) => {
        if (activeTab === 'graded' && !showAllGradedOrders) {
            // Tstatus=ready は入金状況によらず表示
            // Dstatus=ready は total_paymentstatus=true の場合のみ表示
            const needsTWork = order.Tstatus === 'ready';
            const needsDWork = order.Dstatus === 'ready' && order.total_paymentstatus === true;
            if (!needsTWork && !needsDWork) return false;
        }

        if (activeTab !== 'clickpost') {
            return true;
        }

        const excludeByTrouble = order.trouble === true;
        const excludeBySpecialDiploma =
            order.Dstatus === 'ready' && (order.diploma === 'アクリルスタンド' || order.diploma === '証書ケース');
        // Dstatus=ready は total_paymentstatus=true の場合のみ表示
        const excludeByPaymentStatus = order.Dstatus === 'ready' && order.total_paymentstatus !== true;
        // Tstatus=arrive でも Hstatus が arrive/ready/done の場合は除外（Dstatus=ready が同時にある場合は表示優先）
        const excludeByHstatus =
            order.Tstatus === 'arrive' &&
            ['arrive', 'ready', 'done'].includes(order.Hstatus ?? '') &&
            order.Dstatus !== 'ready';
        return !excludeByTrouble && !excludeBySpecialDiploma && !excludeByPaymentStatus && !excludeByHstatus;
    });
    const isGradedTab = activeTab === 'graded';
    const isClickpostTab = activeTab === 'clickpost';

    return (
        <div className="">
            <div className="max-w-7xl mx-auto">
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800 mb-2">Order管理</h1>
                        <p className="text-gray-600">ユーザーと資格の紐付け管理</p>
                    </div>
                    <div className="flex gap-4">
                        <button
                            onClick={handleSyncFromSpreadsheet}
                            disabled={isSyncing}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-300 disabled:cursor-not-allowed"
                        >
                            {isSyncing ? '同期中...' : 'スプレッドシートから同期'}
                        </button>
                        <button
                            onClick={() => setIsAddingOrder(true)}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                            ＋ Order追加
                        </button>
                    </div>
                </div>

                <div className="mb-4 text-sm text-gray-600 font-medium">
                    {getOrderDisplayRange(filteredOrders)}
                </div>

                {activeTab !== 'all' && (
                    <div className="mb-4 flex justify-end items-center gap-4">
                        {isGradedTab && (
                            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={showAllGradedOrders}
                                    onChange={(e) => setShowAllGradedOrders(e.target.checked)}
                                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                すべて表示
                            </label>
                        )}
                        <button
                            onClick={handleDownloadCsv}
                            disabled={isClickpostTab && selectedOrderIds.length === 0}
                            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                        >
                            {isClickpostTab ? 'クリックポスト' : isGradedTab ? '認定証リスト' : 'CSVダウンロード'}
                        </button>
                    </div>
                )}

                <div className="mb-4 flex flex-wrap items-center gap-3">
                    {([
                        { key: 'all', label: 'すべて' },
                        { key: 'shipping', label: '発送' },
                        { key: 'tensaku', label: '添削到着' },
                        { key: 'exam', label: '本試験到着' },
                        { key: 'graded', label: '採点発送' },
                        { key: 'clickpost', label: 'クリックポスト用' }
                    ] as const).map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            onClick={() => handleTabChange(tab.key)}
                            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                                activeTab === tab.key
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div className="mb-4 flex flex-wrap items-end gap-3">
                    <div className="flex flex-col">
                        <label className="text-sm font-medium text-gray-700" htmlFor="orderNumberFilter">
                            注文番号（完全一致）
                        </label>
                        <input
                            id="orderNumberFilter"
                            type="text"
                            value={orderNumberFilter}
                            onChange={(e) => setOrderNumberFilter(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    applyOrderNumberSearch();
                                }
                            }}
                            placeholder="例: ORD-20240101-0001"
                            className="mt-1 w-64 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <button
                        onClick={applyOrderNumberSearch}
                        disabled={loading}
                        className="h-10 rounded-lg bg-blue-600 px-4 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
                    >
                        検索
                    </button>
                    <button
                        onClick={() => {
                            setOrderNumberFilter('');
                            setActiveOrderNumberFilter('');
                        }}
                        disabled={loading || (!orderNumberFilter && !activeOrderNumberFilter)}
                        className="h-10 rounded-lg bg-gray-200 px-4 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-300 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                    >
                        クリア
                    </button>
                </div>

                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                    <div className="overflow-x-auto">
                        {activeTab === 'all' ? (
                            <table className="w-full order-table-compact">
                                <thead className="bg-gray-100 border-b">
                                    <tr>
                                        <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">注文番号</th>
                                        <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">申込日</th>
                                        <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">ユーザー</th>
                                        <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">資格</th>
                                        <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">コース</th>
                                        <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">金額</th>
                                        <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">添削</th>
                                        <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">本試験</th>
                                        <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">発送状況</th>
                                        <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">操作</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {filteredOrders.map((order) => (
                                        <tr key={order.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-4 text-sm text-gray-800 font-mono">
                                                <Link
                                                    to={buildOrderDetailLink(order.id)}
                                                    className="text-blue-600 hover:text-blue-800 hover:underline"
                                                >
                                                    {order.orderNumber}
                                                </Link>
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-600">
                                                {formatOrderDate(order.createdAt)}
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-800">
                                                {renderOrderUserCell(order)}
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-800">{order.shikakuName}</td>
                                            <td className="px-4 py-4 text-sm text-gray-600">{order.course || '-'}</td>
                                            <td className="px-4 py-4 text-sm text-gray-600">
                                                {formatCurrency(order.totalAmount)}
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-600 whitespace-nowrap">
                                                {formatShipmentStatus(order.Tstatus)}
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-600 whitespace-nowrap">
                                                {formatShipmentStatus(order.Hstatus)}
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-600 whitespace-nowrap">
                                                {order.Pstatus === 'done' ? '発送済み' : '未発送'}
                                            </td>
                                            <td className="px-4 py-4">
                                                <button
                                                    onClick={() => handleDeleteOrder(order.id)}
                                                    className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                                                >
                                                    削除
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : activeTab === 'shipping' ? (
                            <table className="w-full order-table-compact">
                                <thead className="bg-gray-100 border-b">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">注文番号</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">申込日</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">ユーザー</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">資格</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">コース</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">付属品</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">金額</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">支払方法</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">操作</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {filteredOrders.map((order) => (
                                        <tr key={order.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 text-sm text-gray-800 font-mono">
                                                <Link
                                                    to={buildOrderDetailLink(order.id)}
                                                    className="text-blue-600 hover:text-blue-800 hover:underline"
                                                >
                                                    {order.orderNumber}
                                                </Link>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600">
                                                {formatOrderDate(order.createdAt)}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-800">
                                                {renderOrderUserCell(order)}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-800">{order.shikakuName}</td>
                                            <td className="px-6 py-4 text-sm text-gray-600">{order.course || '-'}</td>
                                            <td className="px-6 py-4 text-sm text-gray-600">{order.accessories || '-'}</td>
                                            <td className="px-6 py-4 text-sm text-gray-600">
                                                {formatCurrency(order.totalAmount)}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600">
                                                {order.paymentMethod || '-'}
                                            </td>
                                            <td className="px-6 py-4">
                                                <button
                                                    onClick={() => window.open(`/admin/orders/${order.id}/shipping`, '_blank')}
                                                    className="px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 transition-colors"
                                                >
                                                    発送用紙
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : activeTab === 'tensaku' ? (
                            <table className="w-full order-table-compact">
                                <thead className="bg-gray-100 border-b">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">注文番号</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">添削到着日</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">ユーザー</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">資格</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">郵便番号</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">住所</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">操作</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {filteredOrders.map((order) => (
                                        <tr key={order.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 text-sm text-gray-800 font-mono">
                                                <Link
                                                    to={buildOrderDetailLink(order.id)}
                                                    className="text-blue-600 hover:text-blue-800 hover:underline"
                                                >
                                                    {order.orderNumber}
                                                </Link>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600">
                                                {formatOrderDate(order.TarvDate)}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-800">
                                                {renderOrderUserCell(order)}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-800">{order.shikakuName}</td>
                                            <td className="px-6 py-4 text-sm text-gray-600">{order.userPostalCode || '-'}</td>
                                            <td className="px-6 py-4 text-sm text-gray-600">{order.userAddress || '-'}</td>
                                            <td className="px-6 py-4">
                                                <button
                                                    onClick={() => window.open(`/admin/orders/${order.id}/task`, '_blank')}
                                                    className="px-3 py-1 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700 transition-colors"
                                                >
                                                    シート
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : activeTab === 'exam' ? (
                            <table className="w-full order-table-compact">
                                <thead className="bg-gray-100 border-b">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">注文番号</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">本試験到着日</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">ユーザー</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">資格</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">郵便番号</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">住所</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">操作</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {filteredOrders.map((order) => (
                                        <tr key={order.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 text-sm text-gray-800 font-mono">
                                                <Link
                                                    to={buildOrderDetailLink(order.id)}
                                                    className="text-blue-600 hover:text-blue-800 hover:underline"
                                                >
                                                    {order.orderNumber}
                                                </Link>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600">
                                                {formatOrderDate(order.HarvDate)}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-800">
                                                {renderOrderUserCell(order)}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-800">{order.shikakuName}</td>
                                            <td className="px-6 py-4 text-sm text-gray-600">{order.userPostalCode || '-'}</td>
                                            <td className="px-6 py-4 text-sm text-gray-600">{order.userAddress || '-'}</td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-wrap gap-2">
                                                    <button
                                                        onClick={() => window.open(`/admin/orders/${order.id}/exam`, '_blank')}
                                                        className="px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 transition-colors"
                                                    >
                                                        シート
                                                    </button>
                                                    <button
                                                        onClick={() => handleMarkExamDone(order.id)}
                                                        className="px-3 py-1 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700 transition-colors"
                                                    >
                                                        採点完了
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : activeTab === 'clickpost' ? (
                            <table className="w-full order-table-compact">
                                <thead className="bg-gray-100 border-b">
                                    <tr>
                                        <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">
                                            <input
                                                type="checkbox"
                                                checked={isAllVisibleSelected}
                                                onChange={handleToggleAllVisibleSelection}
                                                aria-label="全件選択"
                                                className="h-4 w-4 rounded border-gray-300 text-blue-600"
                                            />
                                        </th>
                                        <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">注文番号</th>
                                        <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">ユーザー名</th>
                                        <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">郵便番号</th>
                                        <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">住所</th>
                                        <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">発送ステータス</th>
                                        <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">添削ステータス</th>
                                        <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">本試験ステータス</th>
                                        <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">認定証タイプ</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {filteredOrders.map((order) => (
                                        <tr key={order.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-4 text-sm text-gray-700">
                                                <input
                                                    type="checkbox"
                                                    checked={isOrderSelected(order.id)}
                                                    onChange={() => handleToggleOrderSelection(order.id)}
                                                    aria-label={`${order.orderNumber}を選択`}
                                                    className="h-4 w-4 rounded border-gray-300 text-blue-600"
                                                />
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-800 font-mono">
                                                <Link
                                                    to={buildOrderDetailLink(order.id)}
                                                    className="text-blue-600 hover:text-blue-800 hover:underline"
                                                >
                                                    {order.orderNumber}
                                                </Link>
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-800">
                                                {order.userId && order.userId !== '-' ? (
                                                    <Link
                                                        to={`/admin/users/${order.userId}`}
                                                        className="text-blue-600 hover:text-blue-800 hover:underline"
                                                    >
                                                        {order.userDisplayName || '-'}
                                                    </Link>
                                                ) : (
                                                    <span>{order.userDisplayName || '-'}</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-600">{order.userPostalCode || '-'}</td>
                                            <td className="px-4 py-4 text-sm text-gray-600">{order.userAddress || '-'}</td>
                                            <td className="px-4 py-4 text-sm text-gray-600">{formatShipmentStatus(order.Pstatus)}</td>
                                            <td className="px-4 py-4 text-sm text-gray-600">{formatShipmentStatus(order.Tstatus)}</td>
                                            <td className="px-4 py-4 text-sm text-gray-600">{formatShipmentStatus(order.Hstatus)}</td>
                                            <td className="px-4 py-4 text-sm text-gray-600">{order.diploma || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <table className="w-full order-table-compact">
                                <thead className="bg-gray-100 border-b">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">注文番号</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">ユーザー</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">資格</th>
                                        {isGradedTab ? (
                                            <>
                                                <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">添削</th>
                                                <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">本試験</th>
                                            </>
                                        ) : (
                                            <>
                                                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">添削追跡番号</th>
                                                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">認定追跡番号</th>
                                            </>
                                        )}
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">郵便番号</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">住所</th>
                                        {isGradedTab && (
                                            <>
                                                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">電話番号</th>
                                                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">認定証</th>
                                            </>
                                        )}
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">操作</th>
                                        {isGradedTab && (
                                            <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">発送通知</th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {filteredOrders.map((order) => {
                                        const isEditing = editingOrderId === order.id && editOrderForm;
                                        const isPurpleDiploma = isGradedTab && (order.diploma === 'アクリルスタンド' || order.diploma === '証書ケース');
                                        const primaryTextColorClass = isPurpleDiploma ? 'text-purple-700' : 'text-gray-800';
                                        const secondaryTextColorClass = isPurpleDiploma ? 'text-purple-700' : 'text-gray-600';
                                        const orderLinkClass = isPurpleDiploma
                                            ? 'text-purple-700 hover:text-purple-800 hover:underline'
                                            : 'text-blue-600 hover:text-blue-800 hover:underline';
                                        return (
                                        <tr
                                            key={order.id}
                                            className={`${isGradedTab && order.total_paymentstatus !== true ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50'} ${isPurpleDiploma ? 'text-purple-700' : ''}`}
                                            onDoubleClick={() => startEditingOrder(order)}
                                        >
                                            <td className={`px-6 py-4 text-sm font-mono ${primaryTextColorClass}`}>
                                                {isEditing ? (
                                                    <input
                                                        value={editOrderForm.orderNumber}
                                                        onChange={(e) => handleEditChange('orderNumber', e.target.value)}
                                                        className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                                                    />
                                                ) : (
                                                    <Link
                                                        to={buildOrderDetailLink(order.id)}
                                                        className={orderLinkClass}
                                                    >
                                                        {order.orderNumber}
                                                    </Link>
                                                )}
                                            </td>
                                            <td className={`px-6 py-4 text-sm ${primaryTextColorClass}`}>
                                                {isEditing ? (
                                                    <select
                                                        value={editOrderForm.userId}
                                                        onChange={(e) => handleEditChange('userId', e.target.value)}
                                                        className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                                                    >
                                                        <option value="">選択してください</option>
                                                        {users.map((user) => (
                                                            <option key={user.id} value={user.id}>
                                                                {user.display_name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    renderOrderUserCell(order, {
                                                        textClassName: primaryTextColorClass,
                                                        linkClassName: orderLinkClass,
                                                    })
                                                )}
                                            </td>
                                            <td className={`px-6 py-4 text-sm ${primaryTextColorClass}`}>
                                                {isEditing ? (
                                                    <select
                                                        value={editOrderForm.shikakuId}
                                                        onChange={(e) => handleEditChange('shikakuId', e.target.value)}
                                                        className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                                                    >
                                                        <option value="">選択してください</option>
                                                        {shikakuList.map((shikaku) => (
                                                            <option key={shikaku.id} value={shikaku.id}>
                                                                {shikaku.ShikakuName}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    order.shikakuName
                                                )}
                                            </td>
                                            {isGradedTab ? (
                                                <>
                                                    <td className={`px-4 py-4 text-sm whitespace-nowrap ${secondaryTextColorClass}`}>
                                                        {formatShipmentStatus(order.Tstatus)}
                                                    </td>
                                                    <td className={`px-4 py-4 text-sm whitespace-nowrap ${secondaryTextColorClass}`}>
                                                        {formatShipmentStatus(order.Hstatus)}
                                                    </td>
                                                </>
                                            ) : (
                                                <>
                                                    <td className="px-6 py-4 text-sm text-gray-600">
                                                        {isEditing ? (
                                                            <input
                                                                value={editOrderForm.TtrackingNumber}
                                                                onChange={(e) => handleEditChange('TtrackingNumber', e.target.value)}
                                                                className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                                                            />
                                                        ) : (
                                                            order.TtrackingNumber || '-'
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-600">
                                                        {isEditing ? (
                                                            <input
                                                                value={editOrderForm.DtrackingNumber}
                                                                onChange={(e) => handleEditChange('DtrackingNumber', e.target.value)}
                                                                className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                                                            />
                                                        ) : (
                                                            order.DtrackingNumber || '-'
                                                        )}
                                                    </td>
                                                </>
                                            )}
                                            <td className={`px-6 py-4 text-sm ${secondaryTextColorClass}`}>
                                                {isEditing ? (
                                                    <input
                                                        value={editOrderForm.userPostalCode}
                                                        onChange={(e) => handleEditChange('userPostalCode', e.target.value)}
                                                        className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                                                    />
                                                ) : (
                                                    order.userPostalCode || '-'
                                                )}
                                            </td>
                                            <td className={`px-6 py-4 text-sm ${secondaryTextColorClass}`}>
                                                {isEditing ? (
                                                    <input
                                                        value={editOrderForm.userAddress}
                                                        onChange={(e) => handleEditChange('userAddress', e.target.value)}
                                                        className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                                                    />
                                                ) : (
                                                    order.userAddress || '-'
                                                )}
                                            </td>
                                            {isGradedTab && (
                                                <>
                                                    <td className={`px-6 py-4 text-sm ${secondaryTextColorClass}`}>
                                                        {formatPhoneNumber(order.userPhoneNumber)}
                                                    </td>
                                                    <td className={`px-6 py-4 text-sm ${secondaryTextColorClass}`}>
                                                        {order.diploma || '-'}
                                                    </td>
                                                </>
                                            )}
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col gap-2">
                                                    {!isGradedTab && (
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={() => window.open(`/admin/orders/${order.id}/task`, '_blank')}
                                                                className="px-3 py-1 bg-emerald-500 text-white text-sm rounded hover:bg-emerald-600 transition-colors"
                                                            >
                                                                添削
                                                            </button>
                                                            <button
                                                                onClick={() => window.open(`/admin/orders/${order.id}/exam`, '_blank')}
                                                                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                                                            >
                                                                本試験
                                                            </button>
                                                        </div>
                                                    )}
                                                    {isEditing && (
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={() => handleSaveEdit(order.id)}
                                                                className="px-3 py-1 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700 transition-colors"
                                                            >
                                                                保存
                                                            </button>
                                                            <button
                                                                onClick={handleCancelEdit}
                                                                className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 transition-colors"
                                                            >
                                                                キャンセル
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            {isGradedTab && (
                                                <td className="px-6 py-4">
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => window.open(`/admin/orders/${order.id}?notify=task`, '_blank')}
                                                            disabled={order.Tstatus !== 'arrive'}
                                                            className={`px-3 py-1 text-sm rounded transition-colors ${
                                                                order.Tstatus === 'arrive'
                                                                    ? 'bg-amber-500 text-white hover:bg-amber-600'
                                                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                            }`}
                                                        >
                                                            添削発送
                                                        </button>
                                                        <button
                                                            onClick={() => window.open(`/admin/orders/${order.id}?notify=diploma`, '_blank')}
                                                            disabled={order.Dstatus !== 'ready'}
                                                            className={`px-3 py-1 text-sm rounded transition-colors ${
                                                                order.Dstatus === 'ready'
                                                                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                                                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                            }`}
                                                        >
                                                            認定発送
                                                        </button>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {filteredOrders.length === 0 && (
                        <div className="text-center py-12 text-gray-500">
                            Orderが登録されていません
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-between mt-4">
                    <button
                        onClick={handleOrderPrevPage}
                        disabled={orderPage === 1 || loading}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:bg-gray-100 disabled:text-gray-400"
                    >
                        前のページ
                    </button>
                    <div className="text-sm text-gray-700 font-medium">Page {orderPage}</div>
                    <button
                        onClick={handleOrderNextPage}
                        disabled={!hasNextOrderPage || loading}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:bg-gray-100 disabled:text-gray-400"
                    >
                        次のページ
                    </button>
                </div>
            </div>

            {isPaymentModalOpen && paymentEditForm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                    <div className="w-full max-w-lg rounded-xl bg-white shadow-lg">
                        <div className="flex items-center justify-between border-b px-6 py-4">
                            <h2 className="text-lg font-semibold text-gray-800">入金情報を編集</h2>
                            <button
                                type="button"
                                onClick={closePaymentModal}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <i className="ri-close-line text-xl" />
                            </button>
                        </div>
                        <div className="space-y-4 px-6 py-5">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">入金期日</label>
                                <input
                                    type="date"
                                    value={paymentEditForm.dueDate}
                                    onChange={(event) =>
                                        setPaymentEditForm((prev) =>
                                            prev ? { ...prev, dueDate: event.target.value } : prev
                                        )
                                    }
                                    className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">入金額</label>
                                <input
                                    type="number"
                                    value={paymentEditForm.amount}
                                    onChange={(event) =>
                                        setPaymentEditForm((prev) =>
                                            prev ? { ...prev, amount: event.target.value } : prev
                                        )
                                    }
                                    className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                                    min="0"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">入金日</label>
                                <input
                                    type="date"
                                    value={paymentEditForm.paidAt}
                                    onChange={(event) =>
                                        setPaymentEditForm((prev) =>
                                            prev ? { ...prev, paidAt: event.target.value } : prev
                                        )
                                    }
                                    className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">ステータス</label>
                                <select
                                    value={paymentEditForm.status}
                                    onChange={(event) =>
                                        setPaymentEditForm((prev) =>
                                            prev
                                                ? { ...prev, status: event.target.value as PaymentEditForm['status'] }
                                                : prev
                                        )
                                    }
                                    className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                                >
                                    <option value="unpaid">未入金</option>
                                    <option value="paid">入金済</option>
                                </select>
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 border-t px-6 py-4">
                            <button
                                type="button"
                                onClick={closePaymentModal}
                                className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
                            >
                                キャンセル
                            </button>
                            <button
                                type="button"
                                onClick={handlePaymentSave}
                                disabled={isPaymentSaving}
                                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {isPaymentSaving ? '保存中...' : '保存'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Order Modal */}
            {isAddingOrder && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b">
                            <h2 className="text-2xl font-bold text-gray-800">Order追加</h2>
                            <p className="text-sm text-gray-600 mt-1">ユーザーと資格を紐付けます</p>
                        </div>

                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    ユーザー <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={newOrderForm.userId}
                                    onChange={(e) => setNewOrderForm({ ...newOrderForm, userId: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="">選択してください</option>
                                    {users.map((user) => (
                                        <option key={user.id} value={user.id}>
                                            {user.display_name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    資格 <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={newOrderForm.shikakuId}
                                    onChange={(e) => setNewOrderForm({ ...newOrderForm, shikakuId: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="">選択してください</option>
                                    {shikakuList.map((shikaku) => (
                                        <option key={shikaku.id} value={shikaku.id}>
                                            {shikaku.ShikakuName}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">ステータス</label>
                                <select
                                    value={newOrderForm.status}
                                    onChange={(e) => setNewOrderForm({ ...newOrderForm, status: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="active">有効</option>
                                    <option value="completed">完了</option>
                                    <option value="cancelled">キャンセル</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">コース</label>
                                <select
                                    value={newOrderForm.course}
                                    onChange={(e) => setNewOrderForm({ ...newOrderForm, course: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="通信講座＋試験">通信講座＋試験</option>
                                    <option value="試験のみ">試験のみ</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">認定証</label>
                                <select
                                    value={newOrderForm.diploma}
                                    onChange={(e) => setNewOrderForm({ ...newOrderForm, diploma: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="通常版">通常版</option>
                                    <option value="証書ケース">証書ケース</option>
                                    <option value="アクリルスタンド">アクリルスタンド</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">支払方法</label>
                                <select
                                    value={newOrderForm.paymentMethod}
                                    onChange={(e) => setNewOrderForm({ ...newOrderForm, paymentMethod: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="クレジット">クレジット</option>
                                    <option value="スマホ決済">スマホ決済</option>
                                    <option value="後払い">後払い</option>
                                    <option value="後払い分割">後払い分割</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">金額</label>
                                <input
                                    type="number"
                                    min={0}
                                    value={newOrderForm.totalAmount}
                                    onChange={(e) => setNewOrderForm({ ...newOrderForm, totalAmount: Number(e.target.value) || 0 })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>


                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="isExamFinished"
                                    checked={newOrderForm.isExamFinished}
                                    onChange={(e) => setNewOrderForm({ ...newOrderForm, isExamFinished: e.target.checked })}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor="isExamFinished" className="text-sm font-medium text-gray-700">
                                    受験済みとして登録
                                </label>
                            </div>

                            <div className="bg-blue-50 border border-blue-200 rounded p-3">
                                <p className="text-sm text-blue-700">
                                    ※ 注文番号は自動生成されます<br />
                                    ※ 申込日は現在日時、有効期限は2年後に自動設定されます
                                </p>
                            </div>
                        </div>

                        <div className="p-6 border-t flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setIsAddingOrder(false);
                                    setNewOrderForm({ userId: '', shikakuId: '', status: 'active', course: '通信講座＋試験', diploma: '通常版', paymentMethod: 'クレジット', totalAmount: 0, isExamFinished: false });
                                }}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                            >
                                キャンセル
                            </button>
                            <button
                                onClick={handleAddOrder}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                            >
                                追加
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Sync Result Modal */}
            {syncResult && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full">
                        <div className="p-6 border-b flex items-center justify-between">
                            <h2 className="text-2xl font-bold text-gray-800">同期結果</h2>
                            <span className="px-3 py-1 text-sm rounded-full bg-green-100 text-green-700">一括同期</span>
                        </div>

                        <div className="p-6 space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {[{ label: 'Users', result: syncResult.users }, { label: 'Order', result: syncResult.orders }].map(({ label, result }) => (
                                    <div key={label} className="border rounded-lg p-4 bg-gray-50">
                                        <div className="flex items-center justify-between mb-3">
                                            <h3 className="text-lg font-semibold text-gray-800">{label}</h3>
                                            <span className="text-xs text-gray-500">{result.errorDetails.length > 0 ? '完了(一部エラー)' : '完了'}</span>
                                        </div>

                                        <div className="grid grid-cols-3 gap-3">
                                            <div className="bg-green-50 border border-green-200 rounded p-3 text-center">
                                                <div className="text-2xl font-bold text-green-700">{result.created}</div>
                                                <div className="text-xs text-green-600">作成</div>
                                            </div>
                                            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-center">
                                                <div className="text-2xl font-bold text-blue-700">{result.updated}</div>
                                                <div className="text-xs text-blue-600">更新</div>
                                            </div>
                                            <div className="bg-red-50 border border-red-200 rounded p-3 text-center">
                                                <div className="text-2xl font-bold text-red-700">{result.errors}</div>
                                                <div className="text-xs text-red-600">エラー</div>
                                            </div>
                                        </div>

                                        {result.errorDetails.length > 0 && (
                                            <div className="mt-4">
                                                <h4 className="font-semibold text-gray-700 mb-2">エラー詳細:</h4>
                                                <div className="bg-red-50 border border-red-200 rounded p-3 max-h-40 overflow-y-auto">
                                                    {result.errorDetails.map((error, index) => (
                                                        <div key={index} className="text-sm text-red-700 mb-1">
                                                            {error}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="p-6 border-t flex justify-end">
                            <button
                                onClick={() => setSyncResult(null)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                閉じる
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
